import React, { useState, useEffect, useCallback } from "react";
import { useSpring, animated } from "react-spring";
import { useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import data from "../main.json";
import lines from "./../media/orange-lines.svg";
import { useAuth, API_URL } from "./../AuthContext";
import deleteButtonIcon from "./../media/delete-button.svg";
import eventBus from "./eventBus";
import { longFormatters } from "date-fns";
import Logger from "../utils/Logger";
import httpClient from "../services/httpClient";
import { useNotification } from "./NotificationContext";
import privateButton from "../media/private-button.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfo } from "@fortawesome/free-solid-svg-icons";
import {
  renderRoomReservationDetails,
  renderCarReservationDetails,
  renderRecurringReservationDetails,
} from "./calendar/DetailViews";

// Miesiące w języku polskim
const polishMonths = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

// Dni tygodnia w języku polskim
const polishWeekDays = ["Pn", "Wt", "Śr", "Cz", "Pt", "Sb", "Nd"];

// Nazwy dni tygodnia w języku polskim
const dayNames = [
  "Poniedziałek",
  "Wtorek",
  "Środa",
  "Czwartek",
  "Piątek",
  "Sobota",
  "Niedziela",
];

function Number({ n }) {
  const { number } = useSpring({
    from: { number: 0 },
    number: n,
    delay: 50,
    config: { mass: 1, tension: 20, friction: 10 },
  });
  return <animated.div>{number.to((n) => n.toFixed(0))}</animated.div>;
}

function PrivateInformation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(window.innerWidth >= 1900);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  // Konfiguracja typów rezerwacji z tłumaczeniami
  const RESERVATION_TYPES = {
    delegation: {
      endpoint: "/CarReservations/upcoming",
      deleteEndpoint: "/delegation/Reservations",
      eventName: "car-reservation-deleted",
      successMessage: t("privateInfo.deleteSuccess.car"),
      title: t("privateInfo.carReservationsTitle"),
      noReservationsMessage: t("privateInfo.noCarReservations"),
    },
    conferences: {
      endpoint: "/RoomReservations/upcoming",
      deleteEndpoint: "/conferences/Reservations",
      eventName: "room-reservation-deleted",
      successMessage: t("privateInfo.deleteSuccess.conference"),
      title: t("privateInfo.conferenceReservationsTitle"),
      noReservationsMessage: t("privateInfo.noConferenceReservations"),
    },
    recurring: {
      endpoint: "/RecurringRoomReservations/owned",
      deleteEndpoint: "/RecurringRoomReservations",
      eventName: "room-reservation-deleted",
      successMessage: t("privateInfo.deleteSuccess.recurring"),
      title: t("privateInfo.recurringReservationsTitle"),
      noReservationsMessage: t("privateInfo.noRecurringReservations"),
    },
  };

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // useEffect do śledzenia czy menu mobilne jest otwarte
  React.useEffect(() => {
    const checkMobileMenuOpen = () => {
      setIsMobileMenuOpen(document.body.classList.contains("mobile-menu-open"));
    };

    // Sprawdź stan początkowy
    checkMobileMenuOpen();

    // Użyj MutationObserver do śledzenia zmian klasy na body
    const observer = new MutationObserver(checkMobileMenuOpen);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const isNarrow = windowWidth < 1900;

  React.useEffect(() => {
    if (windowWidth >= 1900) setOpen(true);
    if (windowWidth < 1900) setOpen(false);
  }, [windowWidth]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [daysInMonth, setDaysInMonth] = useState([]);
  const [workCalendar, setWorkCalendar] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [conferenceReservations, setConferenceReservations] = useState([]);
  const [recurringReservations, setRecurringReservations] = useState([]);
  const [reservationStats, setReservationStats] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reservationToDelete, setReservationToDelete] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const { userData } = useAuth();
  // const { showNotification } = useNotification();

  const getName = () => userData.userName;
  const getRole = () => {
    const roleMap = {
      admin: "Administrator",
      "hr-manager": "Kierownik / HR",
      user: "Użytkownik",
      unknown: "Nieznana",
    };
    return roleMap[userData.userRole] || "Nieznana";
  };
  const getEmail = useCallback(() => userData.userEmail, [userData.userEmail]);

  const formatCalendarDateKey = (dateValue) => {
    const date = new Date(dateValue);
    if (window.Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCalendarDayClass = (dayType) => {
    const normalizedType = String(dayType || "").toLowerCase();

    if (normalizedType.includes("święto") || normalizedType.includes("swieto")) {
      return "holiday";
    }

    if (normalizedType.includes("praca")) {
      return "workday";
    }

    return normalizedType ? normalizedType.replace(/[^a-z0-9-]+/g, "-") : "";
  };

  const calendarDaysByDate = React.useMemo(() => {
    return workCalendar.reduce((acc, item) => {
      const key = formatCalendarDateKey(item.date);
      if (!key) return acc;

      acc[key] = item.dayType;
      return acc;
    }, {});
  }, [workCalendar]);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchWorkCalendar = async () => {
      try {
        Logger.log("Pobieranie kalendarza pracy z /Calendar...");
        const response = await httpClient.get("/Calendar", {
          withAuth: true,
          config: { signal: abortController.signal },
        });

        if (abortController.signal.aborted) {
          return;
        }

        Logger.log("Odpowiedź z /Calendar:", response.data);

        const calendarData = Array.isArray(response.data) ? response.data : [];
        Logger.log("Liczba rekordów z /Calendar:", calendarData.length);
        if (calendarData.length > 0) {
          Logger.log("Pierwszy rekord z /Calendar:", calendarData[0]);
        }

        setWorkCalendar(calendarData);
      } catch (error) {
        if (
          error.name === "AbortError" ||
          error.name === "CanceledError" ||
          error.code === "ERR_CANCELED" ||
          error.code === "ECONNABORTED" ||
          abortController.signal.aborted
        ) {
          return;
        }

        Logger.error("Błąd podczas pobierania kalendarza pracy:", error);
        setWorkCalendar([]);
      }
    };

    fetchWorkCalendar();

    return () => {
      abortController.abort();
    };
  }, []);

  // Wspólna funkcja do pobierania rezerwacji
  const fetchReservations = useCallback(
    async (type) => {
      const userEmail = getEmail();
      if (!userEmail) return;

      try {
        const config = RESERVATION_TYPES[type];
        const response = await httpClient.get(`${config.endpoint}`, {
          withAuth: true,
        });
        const result = response.data;
        Logger.log(`Pobrane rezerwacje ${type}:`, result);

        switch (type) {
          case "delegation":
            setReservations(result);
            break;
          case "conferences":
            setConferenceReservations(result);
            break;
          case "recurring":
            setRecurringReservations(result);
            break;
        }
      } catch (error) {
        Logger.error(`Błąd podczas pobierania rezerwacji ${type}:`, error);
      }
    },
    [getEmail],
  );

  // Funkcja do pobierania statystyk rezerwacji
  const fetchReservationStats = useCallback(async () => {
    const userEmail = getEmail();
    if (!userEmail) {
      return;
    }

    try {
      Logger.log("Pobieranie statystyk rezerwacji dla użytkownika:", userEmail);
      const response = await httpClient.get(
        "/RoomReservations/involved-unconfirmed",
        {
          withAuth: true,
        },
      );

      const result = response.data;
      Logger.log("Otrzymane statystyki rezerwacji:", result);

      // API returns array of reservations directly
      const reservations = Array.isArray(result) ? result : [];
      const stats = {
        notResponded: reservations.length,
        reservations: reservations,
      };

      Logger.log("Przetworzone statystyki:", stats);
      Logger.log("Liczba niepotwierdzonych zaproszeń:", stats.notResponded);

      setReservationStats(stats);
    } catch (error) {
      Logger.error("Błąd podczas pobierania statystyk rezerwacji:", error);
      setReservationStats(null);
    }
  }, [getEmail]);

  // Wspólna funkcja do usuwania rezerwacji
  const removeReservation = async (reservationId, type) => {
    try {
      const config = RESERVATION_TYPES[type];
      await httpClient.delete(`${config.deleteEndpoint}/${reservationId}`, {
        withAuth: true,
      });

      const updateState = (prevReservations) =>
        prevReservations.filter(
          (reservation) => reservation.id !== reservationId,
        );

      switch (type) {
        case "delegation":
          setReservations(updateState);
          break;
        case "conferences":
          setConferenceReservations(updateState);
          break;
        case "recurring":
          setRecurringReservations(updateState);
          break;
      }

      // showNotification(config.successMessage, "success");
      eventBus.emit(config.eventName, {});
      setShowDeleteModal(false);
      setReservationToDelete(null);
    } catch (error) {
      Logger.error("Błąd podczas usuwania rezerwacji:", error);
      // showNotification("Wystąpił błąd podczas usuwania rezerwacji.", "error");
    }
  };

  // Wspólna funkcja do filtrowania rezerwacji według daty
  const filterReservationsByDate = (reservations, dateField, targetDate) => {
    return reservations.filter((r) => {
      const reservationDate = new Date(r[dateField]);
      return isSameDay(reservationDate, targetDate);
    });
  };

  // Wspólna funkcja do filtrowania przyszłych rezerwacji
  const filterFutureReservations = (
    reservations,
    dateField,
    today,
    tomorrow,
  ) => {
    return reservations.filter((r) => {
      const reservationDate = new Date(r[dateField]);
      const isFuture = reservationDate > tomorrow;
      const isNotToday = !isSameDay(reservationDate, today);
      const isNotTomorrow = !isSameDay(reservationDate, tomorrow);
      return isFuture && isNotToday && isNotTomorrow;
    });
  };

  // Wspólny komponent dla wszystkich typów rezerwacji
  const ReservationItem = ({
    reservation,
    highlight,
    title,
    type,
    renderContent,
  }) => {
    const config = RESERVATION_TYPES[type];

    const handleReservationClick = (e) => {
      // Don't trigger click if clicking on delete button
      if (e.target.closest(".close-button")) {
        return;
      }

      // Set up reservation data for detail modal
      let reservationWithType;
      if (type === "delegation") {
        reservationWithType = {
          ...reservation,
          type: "car",
        };
      } else if (type === "recurring") {
        // For recurring reservations template
        reservationWithType = {
          ...reservation,
          type: "recurring",
        };
      } else {
        // For regular conference reservations
        reservationWithType = {
          ...reservation,
          type: "conferenceRoom",
        };
      }

      setSelectedReservation(reservationWithType);
      setShowDetailModal(true);
    };

    return (
      <div
        className={`reservation-item ${
          highlight ? `highlight-${highlight}` : ""
        }`}
        onClick={handleReservationClick}
      >
        <div className="reservation-header">
          <div className="reservation-title">{title}</div>
        </div>
        {renderContent(reservation)}
      </div>
    );
  };

  // Funkcja pomocnicza do renderowania uczestników
  const renderAttendees = (attendees) => {
    if (!attendees) return null;

    let attendeesList = [];

    if (Array.isArray(attendees)) {
      // Jeśli attendees to tablica
      attendeesList = attendees.map((attendee) => {
        if (typeof attendee === "string") {
          return attendee;
        } else if (typeof attendee === "object") {
          return (
            attendee.employee ||
            attendee.name ||
            `${attendee.firstName || ""} ${attendee.lastName || ""}`.trim()
          );
        }
        return String(attendee);
      });
    } else if (typeof attendees === "object") {
      // Jeśli attendees to obiekt
      attendeesList = Object.keys(attendees).filter((key) => {
        // Filtruj klucze które nie są liczbami (indeksami)
        return isNaN(parseInt(key));
      });

      // Jeśli nie ma kluczy-stringów, spróbuj wartości obiektu
      if (attendeesList.length === 0) {
        attendeesList = Object.values(attendees)
          .map((value) => {
            if (typeof value === "string") {
              return value;
            } else if (typeof value === "object" && value !== null) {
              return (
                value.employee ||
                value.name ||
                `${value.firstName || ""} ${value.lastName || ""}`.trim()
              );
            }
            return String(value);
          })
          .filter((name) => name && name !== "");
      }
    }

    return attendeesList.length > 0
      ? attendeesList.join(", ")
      : "Brak uczestników";
  };

  // Funkcje renderowania dla różnych typów rezerwacji
  const renderDelegationContent = (reservation) => (
    <>
      <div>
        <strong>
          {reservation.car.make} {reservation.car.model} {" - "}{" "}
          {reservation.car.licensePlate}
        </strong>
      </div>
      <div>
        <strong>{t("privateInfo.departure")}</strong>{" "}
        {convertDate(reservation.startDateLocal)}
      </div>
      <div>
        <strong>{t("privateInfo.return")}</strong>{" "}
        {convertDate(reservation.endDateLocal)}
      </div>
      <div>
        <strong>{t("privateInfo.city")}</strong> {reservation.location}
      </div>
      <div>
        <strong>{t("privateInfo.purpose")}</strong> {reservation.reason}
      </div>
    </>
  );

  const renderConferenceContent = (reservation) => {
    const startTime = new Date(reservation.startTime);
    const endTime = new Date(reservation.endTime);

    return (
      <>
        <div>
          <strong>{reservation.room}</strong>
        </div>
        <div>
          <strong>{t("privateInfo.reservationDate")}</strong>{" "}
          {startTime.toLocaleDateString("pl-PL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })}
        </div>
        <div>
          <strong>{t("privateInfo.startTime")} </strong>
          {startTime.toLocaleTimeString("pl-PL", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        <div>
          <strong>{t("privateInfo.endTime")} </strong>
          {endTime.toLocaleTimeString("pl-PL", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
        <div>
          <strong>{t("privateInfo.organizer")}</strong> {reservation.createdBy}
        </div>
        <div>
          <strong>{t("privateInfo.purpose")}</strong> {reservation.title}
        </div>
        {reservation.invitations && reservation.invitations.length > 0 && (
          <div>
            <strong>{t("privateInfo.participants")}</strong>{" "}
            {reservation.invitations.map((inv) => inv.userFullName).join(", ")}
          </div>
        )}
      </>
    );
  };

  const renderRecurringContent = (reservation) => {
    const startDate = new Date(reservation.nearestReservationStartDate);
    const endDate = new Date(reservation.nearestReservationEndDate);

    return (
      <>
        <div>
          <strong>{t("privateInfo.room")}</strong> {reservation.room}
        </div>
        <div>
          <strong>{t("privateInfo.title")}</strong> {reservation.title}
        </div>
        <div>
          <strong>{t("privateInfo.nearestReservation")}</strong>{" "}
          {startDate.toLocaleDateString("pl-PL", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })}
          {", "}
          {startDate.toLocaleTimeString("pl-PL", {
            hour: "2-digit",
            minute: "2-digit",
          })}
          {" - "}
          {endDate.toLocaleTimeString("pl-PL", {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </>
    );
  };

  // Wspólny komponent do wyświetlania sekcji rezerwacji
  const ReservationsSection = ({
    reservations,
    type,
    title,
    renderContent,
    todayReservations,
    tomorrowReservations,
    futureReservations,
    noReservationsMessage,
  }) => {
    const hasReservations =
      todayReservations.length > 0 ||
      tomorrowReservations.length > 0 ||
      futureReservations.length > 0;

    if (!hasReservations) {
      return null; // Nie renderuj nic gdy nie ma rezerwacji
    }

    return (
      <div className="reservations-box">
        <div className="reservation-title">{title}</div>

        {todayReservations.length > 0 &&
          todayReservations.map((reservation, index) => (
            <ReservationItem
              key={index}
              reservation={reservation}
              title={t("privateInfo.todayReservations")}
              highlight="red"
              type={type}
              renderContent={renderContent}
            />
          ))}

        {tomorrowReservations.length > 0 &&
          tomorrowReservations.map((reservation, index) => (
            <ReservationItem
              key={index}
              reservation={reservation}
              title={t("privateInfo.tomorrowReservations")}
              highlight="orange"
              type={type}
              renderContent={renderContent}
            />
          ))}

        {futureReservations.length > 0 &&
          futureReservations.map((reservation, index) => (
            <ReservationItem
              key={index}
              reservation={reservation}
              title={t("privateInfo.futureReservations")}
              type={type}
              renderContent={renderContent}
            />
          ))}
      </div>
    );
  };

  // Event handlers
  useEffect(() => {
    const eventHandlers = {
      "car-reserved": () => {
        fetchReservations("delegation");
        fetchReservationStats();
      },
      "room-reserved": () => {
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      "car-reservation-deleted": () => {
        fetchReservations("delegation");
        fetchReservationStats();
      },
      "car-reservation-updated": () => {
        fetchReservations("delegation");
        fetchReservationStats();
      },
      "room-reservation-updated": () => {
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      "room-reservation-deleted": () => {
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      "conference-room-reservation-deleted": () => {
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      "invitation-response-updated": () => {
        fetchReservationStats();
      },
      reFetchRoomReservations: () => {
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      reFetchCarReservations: () => {
        fetchReservations("delegation");
        fetchReservationStats();
      },
    };

    Object.entries(eventHandlers).forEach(([event, handler]) => {
      eventBus.on(event, handler);
    });

    return () => {
      Object.entries(eventHandlers).forEach(([event, handler]) => {
        eventBus.off(event, handler);
      });
    };
  }, [fetchReservations, fetchReservationStats]);

  // Handle ESC key to close detail modal
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && showDetailModal) {
        closeDetailModal();
      }
    };

    if (showDetailModal) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [showDetailModal]);

  useEffect(() => {
    const generateDays = () => {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDayOfMonth = new Date(year, month, 1).getDay();
      const date = new Date(year, month + 1, 0);
      const daysArray = [];

      for (
        let i = 0;
        i < (firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1);
        i++
      ) {
        daysArray.push(null);
      }

      for (let i = 1; i <= date.getDate(); i++) {
        daysArray.push(i);
      }

      return daysArray;
    };

    setDaysInMonth(generateDays());
  }, [currentDate]);

  useEffect(() => {
    fetchReservations("delegation");
    fetchReservationStats();
  }, [fetchReservations, fetchReservationStats]);

  useEffect(() => {
    fetchReservations("conferences");
    fetchReservations("recurring");
  }, [fetchReservations]);

  useEffect(() => {
    const refreshInterval = setInterval(
      () => {
        fetchReservations("delegation");
        fetchReservations("conferences");
        fetchReservations("recurring");
        fetchReservationStats();
      },
      30 * 60 * 1000,
    );

    return () => clearInterval(refreshInterval);
  }, [fetchReservations, fetchReservationStats]);

  const today = new Date().getDate();
  const currentMonthIndex = currentDate.getMonth();
  const currentMonth = polishMonths[currentMonthIndex];
  const currentYear = currentDate.getFullYear();

  const changeMonth = (direction) => {
    setCurrentDate((prevDate) => {
      const newDate = new Date(prevDate);
      let newMonth = newDate.getMonth() + direction;

      if (newMonth < 0) {
        newMonth = 11;
        newDate.setFullYear(newDate.getFullYear() - 1);
      } else if (newMonth > 11) {
        newMonth = 0;
        newDate.setFullYear(newDate.getFullYear() + 1);
      }

      newDate.setMonth(newMonth);
      return newDate;
    });
  };

  const convertDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString("pl-PL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const todayDate = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(todayDate.getDate() + 1);

  const isSameDay = (date1, date2) =>
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear();

  // Filtrowanie rezerwacji
  const todayReservations = filterReservationsByDate(
    reservations,
    "startDateLocal",
    todayDate,
  );
  const tomorrowReservations = filterReservationsByDate(
    reservations,
    "startDateLocal",
    tomorrow,
  );
  const futureReservations = filterFutureReservations(
    reservations,
    "startDateLocal",
    todayDate,
    tomorrow,
  );

  const todayConferenceReservations = filterReservationsByDate(
    conferenceReservations,
    "startTime",
    todayDate,
  );
  const tomorrowConferenceReservations = filterReservationsByDate(
    conferenceReservations,
    "startTime",
    tomorrow,
  );
  const futureConferenceReservations = filterFutureReservations(
    conferenceReservations,
    "startTime",
    todayDate,
    tomorrow,
  );

  const handleDeleteClick = (reservation, type) => {
    setReservationToDelete({ ...reservation, type });
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setReservationToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (reservationToDelete) {
      removeReservation(reservationToDelete.id, reservationToDelete.type);
    }
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedReservation(null);
  };

  const handleNotRespondedClick = (reservation) => {
    // Przygotuj dane rezerwacji dla modalu szczegółów
    const reservationWithType = {
      ...reservation,
      type: "conferenceRoom",
    };

    setSelectedReservation(reservationWithType);
    setShowDetailModal(true);
  };

  const notifySuccess = () => {
    fetchReservations("conferences");
    fetchReservationStats();
    // showNotification("Zapisano odpowiedź na spotkanie.", "success");
  };

  const showError = (message) => {
    // showNotification(message, "error");
  };

  const renderDetailModal = () => {
    if (!showDetailModal || !selectedReservation) return null;

    if (selectedReservation.type === "recurring") {
      return renderRecurringReservationDetails(
        selectedReservation,
        closeDetailModal,
        userData,
        notifySuccess,
        showError,
        navigate,
      );
    } else if (selectedReservation.type === "conferenceRoom") {
      return renderRoomReservationDetails(
        selectedReservation,
        closeDetailModal,
        userData,
        notifySuccess,
        showError,
        navigate,
      );
    } else if (selectedReservation.type === "car") {
      return renderCarReservationDetails(
        selectedReservation,
        closeDetailModal,
        userData,
        notifySuccess,
        showError,
        navigate,
      );
    }
  };

  // Komponent modalu potwierdzenia usunięcia
  const DeleteConfirmationModal = ({
    isOpen,
    onClose,
    onConfirm,
    reservation,
  }) => {
    if (!isOpen || !reservation) return null;

    const isCarReservation = reservation.type === "delegation";
    const isRecurringReservation = reservation.type === "recurring";
    const config = RESERVATION_TYPES[reservation.type];

    const renderRecurringDetails = () => {
      const startDate = new Date(reservation.nearestReservationStartDate);
      const endDate = new Date(reservation.nearestReservationEndDate);

      return (
        <>
          <div className="detail-item">
            <strong>{t("privateInfo.room")}</strong> {reservation.room}
          </div>
          <div className="detail-item">
            <strong>{t("privateInfo.title")}</strong> {reservation.title}
          </div>
          {reservation.description && (
            <div className="detail-item">
              <strong>{t("privateInfo.description")}</strong>{" "}
              {reservation.description}
            </div>
          )}
          <div className="detail-item">
            <strong>{t("privateInfo.nearestReservation")}</strong>{" "}
            {startDate.toLocaleDateString("pl-PL", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
          </div>
          <div className="detail-item">
            <strong>{t("privateInfo.from")}</strong>{" "}
            {startDate.toLocaleTimeString("pl-PL", {
              hour: "2-digit",
              minute: "2-digit",
            })}
            {" - "}
            <strong>{t("privateInfo.to")}</strong>{" "}
            {endDate.toLocaleTimeString("pl-PL", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </>
      );
    };

    return (
      <div className="confirmation-overlay">
        <div className="confirmation-box">
          <img
            className="close-button"
            src={deleteButtonIcon}
            alt="Close"
            onClick={onClose}
          />
          <div className="areYouSure">
            <p>{t("privateInfo.confirmDelete")}</p>
            <div className="reservation-details">
              {isCarReservation ? (
                <>
                  <div className="detail-item">
                    <strong>{t("privateInfo.car")}</strong>{" "}
                    {reservation.car.make} {reservation.car.model}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.departure")}</strong>{" "}
                    {convertDate(reservation.startDateLocal)}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.return")}</strong>{" "}
                    {convertDate(reservation.endDateLocal)}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.city")}</strong>{" "}
                    {reservation.location}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.purpose")}</strong>{" "}
                    {reservation.reason}
                  </div>
                </>
              ) : isRecurringReservation ? (
                renderRecurringDetails()
              ) : (
                <>
                  <div className="detail-item">
                    <strong>{t("privateInfo.room")}</strong> {reservation.room}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.reservationDate")}</strong>{" "}
                    {new Date(reservation.startTime).toLocaleDateString(
                      "pl-PL",
                    )}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.startTime")}</strong>{" "}
                    {new Date(reservation.startTime).toLocaleTimeString(
                      "pl-PL",
                      { hour: "2-digit", minute: "2-digit" },
                    )}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.endTime")}</strong>{" "}
                    {new Date(reservation.endTime).toLocaleTimeString("pl-PL", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.organizer")}</strong>{" "}
                    {reservation.createdBy}
                  </div>
                  <div className="detail-item">
                    <strong>{t("privateInfo.purpose")}</strong>{" "}
                    {reservation.title}
                  </div>
                  {reservation.invitations &&
                    reservation.invitations.length > 0 && (
                      <div className="detail-item">
                        <strong>{t("privateInfo.participants")}</strong>{" "}
                        {reservation.invitations
                          .map((inv) => inv.userFullName)
                          .join(", ")}
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
          <div className="confirmation-buttons">
            <button onClick={onConfirm}>{t("common.yes")}</button>
            <button onClick={onClose}>{t("common.no")}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isNarrow && !open && !isMobileMenuOpen && (
        <button
          className="private-toggle-btn"
          onClick={() => setOpen(true)}
          style={{
            position: "fixed",
            left: 0,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10001,
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
          }}
        >
          <img src={privateButton} alt="Pokaż panel prywatny" />
        </button>
      )}
      <div
        className={`private-information${isNarrow ? " slide-panel" : ""}${
          open ? " open" : ""
        }`}
        style={
          isNarrow
            ? {
                position: "fixed",
                left: open ? 0 : -420,
                top: 0,
                height: "100vh",
                width: 420,
                background: "#171717",
                zIndex: 10001,
                boxShadow: "2px 0 10px rgba(0,0,0,0.2)",
                overflowY: "auto",
                transition: "left 0.4s cubic-bezier(.77,0,.18,1)",
                borderRight: "2px solid #ff9900",
              }
            : {}
        }
      >
        {isNarrow && open && (
          <button
            className="close-button slide-panel-close"
            style={{
              position: "absolute",
              right: 10,
              top: 0,
              background: "none",
              border: "none",
              color: "#ff9900",
              fontSize: 32,
              cursor: "pointer",
              zIndex: 10002,
              padding: 0,
            }}
            onClick={() => setOpen(false)}
          >
            <img
              src={deleteButtonIcon}
              alt="Zamknij panel"
              style={{ width: 20, height: 20 }}
            />
          </button>
        )}
        <div className="box-private">
          <div className="welcome">
            {userData && userData.userName ? (
              <>
                {t("privateInfo.welcome", { name: getName() })}
                <p> {t("privateInfo.yourRole", { role: getRole() })}</p>
                {t("privateInfo.infoText")}
              </>
            ) : (
              <div className="loading-spinner"></div>
            )}
          </div>

          <img src={lines} alt="Lines" className="line-icon" />

          {reservationStats && reservationStats.notResponded > 0 && (
            <div className="reservation-stats">
              <div className="stats-content">
                <span>
                  <Trans
                    i18nKey="privateInfo.unconfirmedInvitations"
                    values={{
                      count: reservationStats.notResponded,
                      invitation:
                        reservationStats.notResponded === 1
                          ? t("privateInfo.invitationSingular")
                          : reservationStats.notResponded < 5
                            ? t("privateInfo.invitationFew")
                            : t("privateInfo.invitationMany"),
                    }}
                    components={{ 1: <strong /> }}
                  />
                </span>
              </div>

              {/* Lista nieodpowiedzianych rezerwacji */}
              {reservationStats.reservations &&
                reservationStats.reservations.length > 0 && (
                  <div className="not-responded-list">
                    <h4>{t("privateInfo.unconfirmedReservations")}</h4>
                    <div className="reservation-items">
                      {reservationStats.reservations.map(
                        (reservation, index) => (
                          <div
                            key={index}
                            className="not-responded-item"
                            onClick={() => handleNotRespondedClick(reservation)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="reservation-info">
                              <strong>
                                {reservation.title ||
                                  t("privateInfo.reservation")}
                                {reservation.startTime && (
                                  <>
                                    {" "}
                                    -{" "}
                                    {new Date(
                                      reservation.startTime,
                                    ).toLocaleDateString("pl-PL", {
                                      day: "numeric",
                                      month: "numeric",
                                    })}
                                  </>
                                )}
                              </strong>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="reservations">
            <ReservationsSection
              reservations={reservations}
              type="delegation"
              title={RESERVATION_TYPES.delegation.title}
              renderContent={renderDelegationContent}
              todayReservations={todayReservations}
              tomorrowReservations={tomorrowReservations}
              futureReservations={futureReservations}
              noReservationsMessage={
                RESERVATION_TYPES.delegation.noReservationsMessage
              }
            />

            <ReservationsSection
              reservations={conferenceReservations}
              type="conferences"
              title={RESERVATION_TYPES.conferences.title}
              renderContent={renderConferenceContent}
              todayReservations={todayConferenceReservations}
              tomorrowReservations={tomorrowConferenceReservations}
              futureReservations={futureConferenceReservations}
              noReservationsMessage={
                RESERVATION_TYPES.conferences.noReservationsMessage
              }
            />

            {recurringReservations.length > 0 && (
              <div className="reservations-box">
                <div className="reservation-title">
                  {RESERVATION_TYPES.recurring.title}
                </div>
                {recurringReservations.map((reservation, index) => (
                  <ReservationItem
                    key={index}
                    reservation={reservation}
                    title={t("privateInfo.recurringReservations")}
                    highlight="orange"
                    type="recurring"
                    renderContent={renderRecurringContent}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="calendar">
            <div className="calendar-header">
              <button className="change-btn" onClick={() => changeMonth(-1)}>
                &lt;
              </button>
              <span className="month-year">
                {currentMonth} {currentYear}
              </span>
              <button className="change-btn" onClick={() => changeMonth(1)}>
                &gt;
              </button>
            </div>
            <div className="calendar-grid">
              {polishWeekDays.map((day, index) => (
                <div key={index} className="calendar-weekday">
                  {day}
                </div>
              ))}
              {daysInMonth.map((day, index) => {
                if (!day) {
                  return <div key={index} className="calendar-day empty" />;
                }

                const dateKey = formatCalendarDateKey(
                  new Date(currentYear, currentMonthIndex, day),
                );
                const dayType = dateKey ? calendarDaysByDate[dateKey] : null;
                const dayTypeClass = getCalendarDayClass(dayType);

                return (
                  <div
                    key={index}
                    className={`calendar-day ${
                      day === today &&
                      currentMonthIndex === new Date().getMonth() &&
                      currentYear === new Date().getFullYear()
                        ? "today"
                        : ""
                    } ${dayTypeClass ? `calendar-day--${dayTypeClass}` : ""}`}
                    title={dayType ? String(dayType) : undefined}
                  >
                    <span className="calendar-day-number">{day}</span>
                    {dayTypeClass === "workday" &&
                      day === today &&
                      currentMonthIndex === new Date().getMonth() &&
                      currentYear === new Date().getFullYear() && (
                        <span
                          className={`calendar-day-marker calendar-day-marker--${dayTypeClass}`}
                          aria-hidden="true"
                        />
                      )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Ilość dni do końca umowy */}
          {/* <div className="daysToEndContract">
            Ilość dni do końca Twojej umowy :
          </div>
          <div className="circle">
            <div className="circle-2">
              <div className="number">
                <Number
                  n={data["private-information"]["numer-end-of-contract"]}
                ></Number>
              </div>
            </div>
          </div> */}

          {/* Ilość wolnych dni do końca roku */}
          {/* <div className="amountOfFreeDays">
            Ilość Twoich dostępnych dni urlopowych :
          </div>
          <div className="circle">
            <div className="circle-2">
              <div className="number">
                <Number
                  n={data["private-information"]["number-yours-free-days"]}
                ></Number>
              </div>
            </div>
          </div> */}

          {/* <div className="daysAtTheEnd">Ilość wolnych dni do końca roku :</div>
          <div className="circle">
            <div className="circle-2">
              <div className="number">
                <Number
                  n={data["private-information"]["number-free-days"]}
                ></Number>
              </div>
            </div>
          </div> */}
        </div>

        {/* Modal potwierdzenia usunięcia */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          reservation={reservationToDelete}
        />

        {/* Modal szczegółów rezerwacji */}
        {renderDetailModal()}
      </div>
    </>
  );
}

export default PrivateInformation;
